import { DatePicker, Descriptions } from "antd";
import moment from "moment";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/useNotification";
import { IPopup } from "../../../models/popup.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import Radio from "../../Radio";
import SelectCampus from "../../SelectCampus";
import Tinymce from "../../Tinymce";

export interface IProps {
  data?: IPopup;
  submit: (data: FormData) => void;
}

const PopupEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const [title, setTitle] = useState('');
  const [openYn, setOpenYn] = useState('Y');
  const [content, setContent] = useState('');

  const [positionLeft, setPositionLeft] = useState('');
  const [positionTop, setPositionTop] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showOrder, setShowOrder] = useState('0');

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setOpenYn(data.openYn);
      setContent(data.content);
      setPositionLeft(data.positionLeft);
      setPositionTop(data.positionTop);
      setStartDate(data.startDate);
      setEndDate(data.endDate);
      setShowOrder(String(data.showOrder));
    }
  }, [data]);

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!content) {
      return infoNotification(t('homepage.archive.message4'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('campusCode', currentCampus);
    form.append('positionLeft', positionLeft);
    form.append('positionTop', positionTop);
    form.append('startDate', startDate);
    form.append('endDate', endDate);
    form.append('openYn', openYn);
    form.append('content', content);
    form.append('showOrder', showOrder);
    submit(form);
  }

  return (
    <div className="main">
      <Descriptions
        bordered
        column={4}
        size="small"
      >
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={4}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.writer')}>
          {auth?.korName}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column8')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('event.education.column11')}>
          <div className="w-150p">
            <SelectCampus />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.popup.text2')}>
          <input
            type="text"
            className="form-control pointer"
            value={showOrder}
            onChange={(e) => setShowOrder(e.target.value)}
          />
        </Descriptions.Item>

        <Descriptions.Item label={t('homepage.popup.text3')}>
          <input
            type="text"
            className="form-control pointer"
            value={positionLeft}
            onChange={(e) => setPositionLeft(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.popup.text4')}>
          <input
            type="text"
            className="form-control pointer"
            value={positionTop}
            onChange={(e) => setPositionTop(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.popup.text5')}>
          <div className="date-picker__area">
            <DatePicker
              className="mr-1"
              value={startDate ? moment(startDate) : undefined}
              onChange={(e) => {
                if (e === null) setStartDate('')
                else setStartDate(moment(e).format('YYYYMMDD'));
              }}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.popup.text6')}>
          <div className="date-picker__area">
            <DatePicker
              className="mr-1"
              value={endDate ? moment(endDate) : undefined}
              onChange={(e) => {
                if (e === null) setEndDate('');
                else setEndDate(moment(e).format('YYYYMMDD'));
              }}
            />
          </div>
        </Descriptions.Item>

        <Descriptions.Item label={`* ${t('common.column.contents')}`} span={4}>
          <Tinymce
            content={content}
            setContent={setContent}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.popup.text7')}>
          <Radio
            checked={openYn === 'Y'}
            onChange={() => setOpenYn('Y')}
            text={t('common.option.yes')}
          />
          <Radio
            className="ml-4"
            checked={openYn === 'N'}
            onChange={() => setOpenYn('N')}
            text={t('common.option.no')}
          />
        </Descriptions.Item>
      </Descriptions>

      <div className="mx-auto mt-4 mb-2 w-210p">
        <Button
          onClick={onSubmit}
          className="btn btn-sm btn-primary btn-w100p mr-1"
          text={t('common.button.save')}
          writeAuthYn={writeAuthYn}
        />
        <Button
          onClick={() => history.push(`/homepage/popup${location.search}`)}
          className="btn btn-sm btn-secondary btn-w100p"
          text={t('common.button.cancel')}
        />
      </div>
    </div>
  );
}

export default PopupEditor;
